import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, API_BASE } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowLeft, FileText, ExternalLink, Search } from "lucide-react";
import usePageTitle from "@/lib/usePageTitle";
import { formatLongDate } from "@/lib/dates";

const fmt = (d) => {
  if (!d || d === "2000-01-01") return "Date unknown";
  return formatLongDate(d);
};

export const MeetingMinutesList = () => {
  usePageTitle("Meeting Minutes");
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/minutes").then((res) => setItems(res.data)).catch(() => {});
  }, []);

  const filtered = items.filter(
    (m) =>
      m.title.toLowerCase().includes(q.toLowerCase()) ||
      (m.summary || "").toLowerCase().includes(q.toLowerCase()),
  );

  const byYear = filtered.reduce((acc, m) => {
    const y = (m.meeting_date || "").slice(0, 4);
    const bucket = !y || y === "2000" ? "Undated" : y;
    (acc[bucket] = acc[bucket] || []).push(m);
    return acc;
  }, {});
  const years = Object.keys(byYear).sort((a, b) => {
    if (a === "Undated") return 1;
    if (b === "Undated") return -1;
    return b.localeCompare(a);
  });

  return (
    <div className="container-le section-pad" data-testid="minutes-page">
      <div className="max-w-2xl mb-10">
        <div className="text-xs uppercase tracking-[0.25em] text-secondary mb-3">Archive</div>
        <h1 className="font-heading text-4xl sm:text-5xl font-medium tracking-tight">Meeting Minutes</h1>
        <p className="text-muted-foreground mt-4 leading-relaxed">
          Records of Lake Eunice Township Board meetings. Click any entry to read the full minutes.
        </p>
      </div>

      <div className="relative max-w-md mb-10">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by title or summary..."
          className="pl-9"
          data-testid="minutes-search-input"
        />
      </div>

      {years.length === 0 && <div className="text-muted-foreground">No minutes posted yet.</div>}

      <div className="space-y-12">
        {years.map((y) => (
          <div key={y}>
            <div className="text-xs uppercase tracking-[0.2em] text-accent mb-4">{y}</div>
            <Card className="border-border">
              <CardContent className="p-0 divide-y divide-border">
                {byYear[y].map((m) => (
                  <Link
                    to={`/minutes/${m.id}`}
                    key={m.id}
                    className="flex items-center justify-between gap-4 p-5 hover:bg-muted/40 transition-colors group"
                    data-testid={`minutes-link-${m.id}`}
                  >
                    <div className="flex items-start gap-4">
                      <FileText className="text-secondary mt-1 shrink-0" size={18} />
                      <div>
                        <div className="font-medium group-hover:text-primary">{m.title}</div>
                        {m.summary && <div className="text-sm text-muted-foreground mt-0.5">{m.summary}</div>}
                      </div>
                    </div>
                    <div className="text-xs text-muted-foreground shrink-0">{fmt(m.meeting_date)}</div>
                  </Link>
                ))}
              </CardContent>
            </Card>
          </div>
        ))}
      </div>
    </div>
  );
};

export const MeetingMinuteDetail = () => {
  const { id } = useParams();
  const nav = useNavigate();
  const [item, setItem] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(`/minutes/${id}`)
      .then((res) => setItem(res.data))
      .catch(() => setError("Minutes not found."));
  }, [id]);

  if (error) {
    return (
      <div className="container-le section-pad text-center">
        <div className="text-muted-foreground">{error}</div>
        <Button className="mt-4" onClick={() => nav("/minutes")}>Back to minutes</Button>
      </div>
    );
  }
  if (!item) return <div className="container-le section-pad text-center text-muted-foreground">Loading...</div>;

  return (
    <div className="container-le section-pad max-w-3xl" data-testid="minutes-detail-page">
      <Link to="/minutes" className="inline-flex items-center gap-1.5 text-sm text-secondary mb-6 link-underline">
        <ArrowLeft size={14} /> All meeting minutes
      </Link>
      <div className="text-xs uppercase tracking-[0.2em] text-accent mb-3">{fmt(item.meeting_date)}</div>
      <h1 className="font-heading text-3xl sm:text-4xl font-medium tracking-tight">{item.title}</h1>
      {item.summary && <p className="text-muted-foreground mt-4 text-lg leading-relaxed">{item.summary}</p>}

      {(item.pdf_file_id || item.pdf_url) && (
        <a
          href={item.pdf_file_id ? `${API_BASE}/files/${item.pdf_file_id}` : item.pdf_url}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-2 mt-6 px-4 py-2 rounded-md bg-secondary text-secondary-foreground text-sm font-medium hover:bg-secondary/90"
          data-testid="minutes-pdf-link"
        >
          <ExternalLink size={14} /> {item.pdf_file_id ? "Open uploaded document" : "Open document"}
        </a>
      )}

      <div className="mt-10 prose-le whitespace-pre-wrap text-[1.02rem] leading-relaxed text-foreground/85">
        {item.content || "Full text of the minutes will be added here."}
      </div>
    </div>
  );
};
